/**
 * Subtle, static background accents for landing sections.
 *
 * What: Renders low-contrast decorative shapes behind content without animation.
 * Why: Adds gentle brand texture while avoiding gradients/glass and respecting reduced-motion preferences by default.
 * When: Update when changing the landing visual language or section background treatments.
 */
import { Box } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';

import { colors } from '@/components/landing/landingTokens';

interface LandingBackgroundAccentProps {
  sx?: SxProps<Theme>;
}

function LandingBackgroundAccent({ sx }: LandingBackgroundAccentProps) {
  return (
    <Box
      aria-hidden
      sx={{
        pointerEvents: 'none',
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
        ...sx,
      }}
    >
      {/* Top-right soft "ink" ring */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: -64, md: -90 },
          right: { xs: -64, md: -90 },
          width: { xs: 220, md: 300 },
          height: { xs: 220, md: 300 },
          borderRadius: 9999,
          backgroundColor: alpha(colors.accentCobalt, 0.06),
          border: `1px solid ${alpha(colors.accentCobalt, 0.16)}`,
        }}
      />

      {/* Bottom-left warm wash */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: -78, md: -110 },
          left: { xs: -78, md: -110 },
          width: { xs: 260, md: 340 },
          height: { xs: 260, md: 340 },
          borderRadius: 9999,
          backgroundColor: alpha(colors.accentOrange, 0.08),
          border: `1px solid ${alpha(colors.accentOrange, 0.14)}`,
        }}
      />

      {/* Small editorial block (adds "print" feel without noise) */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 18, md: 22 },
          left: { xs: 18, md: 24 },
          width: { xs: 86, md: 110 },
          height: { xs: 10, md: 12 },
          borderRadius: 9999,
          backgroundColor: alpha(colors.ink, 0.08),
        }}
      />
    </Box>
  );
}

export default LandingBackgroundAccent;
